/**
 * utteranceCapture — 全局共享的「整句采集」核心（P0/2026-10-03）
 *
 * 从 genesis `useAudioCapture` 移植 PCM/VAD/WAV 纯核心（字节级搬运 + 参数化）。
 * 两个消费方：
 *  - genesis 游戏说话链（继续带 Opus 副通道，本核心只负责整句 WAV）
 *  - 全局输入框语音输入（imStore → voice_input_transcribe）
 * 契约：devdocs/specs/SPEC_DESKTOP_GLOBAL_INPUT_VOICE_STT_ROUTING_2026-10-03.md §4.1
 *
 * 关键语义（与 genesis 原实现逐条对齐）：
 *  - 输出 16kHz/16bit/mono WAV（44 字节头）；AudioContext 实际采样率非 16k 时
 *    先低通再降采样（**降采样守卫**：此前按 16k 假设会让非 16k 设备上的
 *    时间戳与音高整体错位且不报错）。
 *  - 能量 VAD：起说 RMS>0.025、判停 <0.012 连续 5 块（≈1.28s）、pre-roll 2 块
 *    （512ms，防削字头声母）、单句上限 59 块（≈15s，防内存失控）。
 *  - stop() 丢弃半句：半句的转写比"没有"更有害（真文本但语义残缺）。
 */

// ─── 采样与 VAD 常量（导出以便单测与调参；调门改这里） ────────────────────────

export const VAD_START_RMS = 0.025;
export const VAD_STOP_RMS = 0.012;
/** 判停需要的连续静音块数（256ms/块 ⇒ ≈1.28s） */
export const VAD_SILENCE_CHUNKS = 5;
/** 起说回看块数（pre-roll，256ms/块 ⇒ 512ms） */
export const VAD_PREROLL_CHUNKS = 2;
/** 单句上限块数（256ms/块 ⇒ ≈15s，防占用内存与超长循环） */
export const VAD_MAX_CHUNKS = 59;
/** 处理器块大小 @16kHz 单声道 */
export const PCM_CHUNK_SIZE = 4096;
export const PCM_SAMPLE_RATE = 16000;

// ─── 纯函数（单测面） ────────────────────────────────────────────────────────

/** 计算 16kHz/mono Float32 块的 RMS。 */
export function chunkRms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    const v = samples[i];
    sum += v * v;
  }
  return Math.sqrt(sum / samples.length);
}

/** Float32 PCM [-1,1] → 16kHz/16bit/mono WAV（44 字节头）字节精确包。 */
export function encodeWav16kMono(samples: Float32Array): ArrayBuffer {
  const dataLen = samples.length * 2;
  const buf = new ArrayBuffer(44 + dataLen);
  const view = new DataView(buf);
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataLen, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // format = PCM
  view.setUint16(22, 1, true); // channels
  view.setUint32(24, PCM_SAMPLE_RATE, true);
  view.setUint32(28, PCM_SAMPLE_RATE * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataLen, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    off += 2;
  }
  return buf;
}

// ─── 降采样守卫（设备采样率 ≠ 16k 时） ──────────────────────────────────────

/** 低通截止 7.2kHz（< 8kHz Nyquist，留过渡带防混叠折回）。 */
const FIR_CUTOFF_HZ = 7200;
const FIR_TAPS = 33;
const firCache = new Map<number, Float32Array>();

/** Hamming 窗 sinc 低通核，直流增益归一为 1（不整体衰减语音）。 */
function firTaps(srcRate: number): Float32Array {
  let taps = firCache.get(srcRate);
  if (taps) return taps;
  taps = new Float32Array(FIR_TAPS);
  const fc = FIR_CUTOFF_HZ / srcRate; // 归一化（cycles/sample）
  const m = (FIR_TAPS - 1) / 2;
  let sum = 0;
  for (let n = 0; n < FIR_TAPS; n++) {
    const x = n - m;
    const val =
      x === 0
        ? 2 * fc
        : Math.sin(2 * Math.PI * fc * x) / (Math.PI * x);
    const w = 0.54 - 0.46 * Math.cos((2 * Math.PI * n) / (FIR_TAPS - 1));
    taps[n] = val * w;
    sum += taps[n];
  }
  for (let n = 0; n < FIR_TAPS; n++) taps[n] /= sum;
  firCache.set(srcRate, taps);
  return taps;
}

function firLowpass(x: Float32Array, srcRate: number): Float32Array {
  const taps = firTaps(srcRate);
  const m = (taps.length - 1) / 2;
  const out = new Float32Array(x.length);
  for (let i = 0; i < x.length; i++) {
    let acc = 0;
    for (let k = 0; k < taps.length; k++) {
      const idx = i - k + m;
      if (idx >= 0 && idx < x.length) acc += taps[k] * x[idx];
    }
    out[i] = acc;
  }
  return out;
}

/**
 * 任意设备采样率 Float32 块 → 16kHz（线性插值 + 前置低通）。
 * srcRate === 16000 时原样返回（零成本快捷路径）。
 */
export function resampleTo16k(chunk: Float32Array, srcRate: number): Float32Array {
  if (srcRate === PCM_SAMPLE_RATE) return chunk;
  const filtered = srcRate > PCM_SAMPLE_RATE ? firLowpass(chunk, srcRate) : chunk;
  const outLen = Math.floor((filtered.length * PCM_SAMPLE_RATE) / srcRate);
  const out = new Float32Array(outLen);
  const step = srcRate / PCM_SAMPLE_RATE;
  for (let i = 0; i < outLen; i++) {
    const pos = i * step;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, filtered.length - 1);
    const frac = pos - i0;
    out[i] = filtered[i0] * (1 - frac) + filtered[i1] * frac;
  }
  return out;
}

// ─── VAD 状态机（无 WebAudio 依赖，可纯单测） ────────────────────────────────

export interface VadParams {
  /** 起说阈值 */
  startRms: number;
  /** 判停阈值 */
  stopRms: number;
  /** 判停所需连续静音块数 */
  silenceChunks: number;
  /** 起说回看块数（pre-roll） */
  prerollChunks: number;
  /** 单句上限块数 */
  maxChunks: number;
  /** 块采样率（WAV 与时长换算用） */
  sampleRate: number;
}

export const DEFAULT_VAD_PARAMS: VadParams = {
  startRms: VAD_START_RMS,
  stopRms: VAD_STOP_RMS,
  silenceChunks: VAD_SILENCE_CHUNKS,
  prerollChunks: VAD_PREROLL_CHUNKS,
  maxChunks: VAD_MAX_CHUNKS,
  sampleRate: PCM_SAMPLE_RATE,
};

export interface VadMachineOptions {
  /** 整句落定：16k/16bit/mono WAV 字节 + 时长（ms） */
  onUtterance: (wav: ArrayBuffer, durationMs: number) => void;
  /** "正在说话"指示（供 UI HUD） */
  onListeningChange?: (listening: boolean) => void;
  params?: Partial<VadParams>;
}

export interface VadMachine {
  push(chunk: Float32Array): { finalized: boolean; utteranceMs: number };
  /** 丢弃当前未落定的整句（停采集时用：半句不打给 STT） */
  reset(): void;
  readonly listening: boolean;
}

/** 从 genesis 闭包状态机逐行移植（行为保持不变，改为显式对象便于两处复用与单测）。 */
export function createVadMachine(opts: VadMachineOptions): VadMachine {
  const p: VadParams = { ...DEFAULT_VAD_PARAMS, ...(opts.params || {}) };
  let speaking = false;
  let silence = 0;
  let utterance: Float32Array[] = [];
  let preroll: Float32Array[] = [];
  let listening = false;

  const setListening = (v: boolean) => {
    if (v === listening) return;
    listening = v;
    opts.onListeningChange?.(v);
  };

  /** 打包当前 utterance 成 WAV 交给回调，复位状态机。 */
  function finalize(): number {
    const total = utterance.reduce((n, c) => n + c.length, 0);
    const merged = new Float32Array(total);
    let off = 0;
    for (const c of utterance) {
      merged.set(c, off);
      off += c.length;
    }
    utterance = [];
    speaking = false;
    silence = 0;
    setListening(false);
    const durationMs = Math.round((total / p.sampleRate) * 1000);
    if (total > 0) opts.onUtterance(encodeWav16kMono(merged), durationMs);
    return durationMs;
  }

  function push(chunk: Float32Array): { finalized: boolean; utteranceMs: number } {
    const rms = chunkRms(chunk);
    if (!speaking) {
      preroll.push(chunk);
      if (preroll.length > p.prerollChunks) preroll.shift();
      if (rms >= p.startRms) {
        speaking = true;
        silence = 0;
        utterance = [...preroll];
        preroll = [];
        setListening(true);
      }
      return { finalized: false, utteranceMs: 0 };
    }
    // 已在说：累计
    utterance.push(chunk);
    if (rms < p.stopRms) {
      silence++;
      if (silence >= p.silenceChunks) {
        return { finalized: true, utteranceMs: finalize() };
      }
    } else {
      silence = 0;
    }
    if (utterance.length >= p.maxChunks) {
      // 上限截断：15s 说不停（对着麦念课文）就先出这句
      return { finalized: true, utteranceMs: finalize() };
    }
    return { finalized: false, utteranceMs: 0 };
  }

  function reset(): void {
    utterance = [];
    preroll = [];
    speaking = false;
    silence = 0;
    setListening(false);
  }

  return {
    push,
    reset,
    get listening() {
      return listening;
    },
  };
}

// ─── WebAudio 粘连层 ─────────────────────────────────────────────────────────

export interface CaptureCallbacks {
  onUtterance: (wav: ArrayBuffer, durationMs: number) => void;
  /** 每块回调（降采样后 16k 域的 RMS，0..1 量级，供电平表/遥测） */
  onLevel?: (rms: number) => void;
  onListeningChange?: (listening: boolean) => void;
  params?: Partial<VadParams>;
}

export interface AttachedCapture {
  /** 入流实际采样率（守卫是否触发以它为准） */
  readonly sampleRate: number;
  stop(): void;
}

/**
 * 把整句采集挂到**已存在**的 AudioContext + MediaStream 上（调用方持有二者生命周期）。
 * genesis 游戏说话链用这个形态（它还要共用同一个 ctx 做 stress 分析）。
 */
export function attachUtteranceCapture(
  context: AudioContext,
  stream: MediaStream,
  opts: CaptureCallbacks
): AttachedCapture {
  const source = context.createMediaStreamSource(stream);
  const processor = context.createScriptProcessor(PCM_CHUNK_SIZE, 1, 1);
  const zeroGain = context.createGain();
  zeroGain.gain.value = 0; // 接到 destination 才推流；0 增益防回声
  const machine = createVadMachine({
    onUtterance: opts.onUtterance,
    onListeningChange: opts.onListeningChange,
    params: { ...opts.params, sampleRate: PCM_SAMPLE_RATE },
  });

  processor.onaudioprocess = (ev: AudioProcessingEvent) => {
    // 必须拷贝：ScriptProcessor 的 inputBuffer 回调后被引擎复用，
    // 直接把 subarray 句柄塞进 utterance 会看到"下一块覆盖上一块"——
    // WAV 出来是同一段噪声的循环，而且不报错。
    const input = ev.inputBuffer.getChannelData(0);
    const copy = new Float32Array(input.length);
    copy.set(input);
    const chunk =
      context.sampleRate === PCM_SAMPLE_RATE
        ? copy
        : resampleTo16k(copy, context.sampleRate);
    opts.onLevel?.(chunkRms(chunk));
    machine.push(chunk);
  };
  source.connect(processor);
  processor.connect(zeroGain);
  zeroGain.connect(context.destination);

  let stopped = false;
  return {
    get sampleRate() {
      return context.sampleRate;
    },
    stop() {
      if (stopped) return;
      stopped = true;
      try {
        processor.disconnect();
      } catch { /* already disconnected */ }
      processor.onaudioprocess = null;
      try {
        zeroGain.disconnect();
      } catch { /* noop */ }
      try {
        source.disconnect();
      } catch { /* noop */ }
      // 中途停采集：话说到一半直接放弃（不打半句给 STT）
      machine.reset();
    },
  };
}

/** AudioWorklet 采集是否可用（纯判据，测试面；真机 WebKitGTK 2.40+ 支持）。 */
export function workletCaptureAvailable(context: AudioContext): boolean {
  return !!(context as unknown as { audioWorklet?: unknown }).audioWorklet &&
    typeof (globalThis as unknown as { AudioWorkletNode?: unknown }).AudioWorkletNode === 'function';
}

/**
 * [spec §4.13] 采集挂接（优先 AudioWorklet）：worklet 负责攒块与转移缓冲，
 * **VAD 状态机仍是主线程单真源**（零逻辑重复）；任何一环不可用即**原路回落**
 * ScriptProcessor（行为与既有 attachUtteranceCapture 完全一致）。
 */
export async function attachUtteranceCapturePreferred(
  context: AudioContext,
  stream: MediaStream,
  opts: CaptureCallbacks
): Promise<AttachedCapture> {
  if (workletCaptureAvailable(context)) {
    try {
      const url = new URL('../../worklets/vad-capture-processor.js', import.meta.url);
      await (context as unknown as { audioWorklet: { addModule(u: string | URL): Promise<void> } })
        .audioWorklet.addModule(url);
      const NodeCtor = (globalThis as unknown as {
        AudioWorkletNode: new (
          ctx: BaseAudioContext,
          name: string,
          opt?: { numberOfInputs?: number; numberOfOutputs?: number; processorOptions?: unknown }
        ) => AudioWorkletNode;
      }).AudioWorkletNode;
      const node = new NodeCtor(context, 'vad-capture', {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        processorOptions: { chunkSize: PCM_CHUNK_SIZE },
      });
      const source = context.createMediaStreamSource(stream);
      const machine = createVadMachine({
        onUtterance: opts.onUtterance,
        onListeningChange: opts.onListeningChange,
        params: { ...opts.params, sampleRate: PCM_SAMPLE_RATE },
      });
      node.port.onmessage = (ev: MessageEvent) => {
        const raw = ev.data;
        const chunk = raw instanceof Float32Array ? raw : new Float32Array(raw as ArrayBuffer);
        const resampled =
          context.sampleRate === PCM_SAMPLE_RATE
            ? chunk
            : resampleTo16k(chunk, context.sampleRate);
        opts.onLevel?.(chunkRms(resampled));
        machine.push(resampled);
      };
      source.connect(node);
      let stopped = false;
      return {
        get sampleRate() {
          return context.sampleRate;
        },
        stop() {
          if (stopped) return;
          stopped = true;
          try {
            node.port.postMessage('stop');
          } catch { /* 端口已关 */ }
          try {
            node.disconnect();
          } catch { /* noop */ }
          try {
            source.disconnect();
          } catch { /* noop */ }
          machine.reset(); // 中途停：丢弃半句（与 ScriptProcessor 路径同语义）
        },
      };
    } catch (e) {
      console.warn('[utteranceCapture] AudioWorklet 路径不可用，回落 ScriptProcessor：', e);
    }
  }
  return attachUtteranceCapture(context, stream, opts);
}

export type MicCaptureErrorCode = 'MIC_UNAVAILABLE' | 'MIC_DENIED' | 'AUDIO_SETUP_FAILED';

export class MicCaptureError extends Error {
  readonly code: MicCaptureErrorCode;
  constructor(code: MicCaptureErrorCode, message?: string) {
    super(message || code);
    this.name = 'MicCaptureError';
    this.code = code;
  }
}

/**
 * 自管生命周期的采集（getUserMedia + AudioContext 全由本函数持有，stop() 全释放）。
 * 全局输入框语音输入用这个形态。
 *
 * 失败语义（诚实铁律 L2）：抛 MicCaptureError，**不**降级成静音假流。
 * 调用方必须把 code 映射为显式用户提示（i18n）。
 */
export async function startMicCapture(
  opts: CaptureCallbacks
): Promise<{ stop(): void; readonly sampleRate: number }> {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new MicCaptureError(
      'MIC_UNAVAILABLE',
      'navigator.mediaDevices unavailable in this WebView'
    );
  }
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        channelCount: 1,
      },
    });
  } catch (e: any) {
    throw new MicCaptureError('MIC_DENIED', e?.message || 'microphone permission denied');
  }

  let context: AudioContext;
  const Ctx: typeof AudioContext =
    window.AudioContext || (window as any).webkitAudioContext;
  try {
    // 优先请求 16k（可省一次重采样）；未获准时由守卫兜底——
    // 不假设请求一定生效（WebView 不保证）。
    context = new Ctx({ sampleRate: PCM_SAMPLE_RATE });
  } catch {
    try {
      context = new Ctx();
    } catch (e: any) {
      stream.getTracks().forEach((t) => t.stop());
      throw new MicCaptureError('AUDIO_SETUP_FAILED', e?.message || 'AudioContext creation failed');
    }
  }

  let attached: AttachedCapture;
  try {
    attached = await attachUtteranceCapturePreferred(context, stream, opts);
  } catch (e: any) {
    stream.getTracks().forEach((t) => t.stop());
    context.close().catch(() => {});
    throw new MicCaptureError('AUDIO_SETUP_FAILED', e?.message || 'audio pipeline setup failed');
  }

  let stopped = false;
  return {
    get sampleRate() {
      return attached.sampleRate;
    },
    stop() {
      if (stopped) return;
      stopped = true;
      attached.stop();
      stream.getTracks().forEach((t) => t.stop());
      context.close().catch(() => {});
    },
  };
}
